import { TaskListItem, taskListQuery } from "@/app/Tasks/GraphQL/taskListQuery";
import { CellContext } from "@/lib/Components/DataTable/types";
import {
  QueryTasksOrderByColumn,
  QueryTasksOrderByOrderByClause,
  RefundType,
  SortOrder,
  TaskListQueryVariables,
  TaskStatus,
  TaskType,
} from "@/gql/graphql";
import { formatDateTimeFromNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { Link } from "@/components/catalyst/link";
import { cn } from "@/lib/utils";
import { Text } from "@/components/catalyst/text";
import { useTranslations } from "use-intl";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import { useTaskActions } from "@/app/Tasks/Hooks/useTaskActions";
import { Badge } from "@/components/catalyst/badge";
import { taskStatusMap } from "@/app/Tasks/Utils/taskStatusMap";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BookingCompleteTaskDialog } from "@/app/Tasks/Components/BookingCompleteTaskDialog";
import { ProcessRefundDialog } from "@/app/Refunds/Components/ProcessRefundDialog";
import { ProcessDriverPayoutDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ProcessDriverPayoutDialog";
import { ApproveReceiptsDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ApproveReceiptsDialog";
import { BookingSupplierConfirmationReviewDialog } from "@/app/Bookings/Components/Dialogs/BookingSupplierConfirmationReviewDialog";
import { RelocationLineEmailReviewDialog } from "@/app/Relocations/Components/Dialogs/RelocationLineEmailReviewDialog";

type TaskListColumnId =
  | "item"
  | "contact"
  | "due_at"
  | "status"
  | "related_to_type"
  | "related_to_id"
  | "relatedTo"
  | "type"
  | "description"
  | "created_at"
  | "updated_at"
  | "actions";

export function TaskList({
  queryVariables,
  id,
}: {
  id: TableId;
  queryVariables?: Partial<Omit<TaskListQueryVariables, "page" | "first">>;
}) {
  const t = useTranslations("task");
  const getActions = useTaskActions();
  const { isAdmin } = useTenant();

  const columns: DataTableColDef<TaskListItem, any, TaskListColumnId>[] = [
    {
      id: "item",
      header: t("table.item"),
      cell: RelatedToCell,
    },
    {
      id: "contact",
      header: t("table.supplier"),
      cell: ({ row }) => {
        const relatedTo = row.original.relatedTo;
        const supplier =
          relatedTo?.__typename === "Booking"
            ? relatedTo.relocation.supplier
            : relatedTo?.__typename === "BookingSupplierConfirmation"
              ? (relatedTo.booking?.relocation.supplier ?? null)
              : relatedTo?.__typename === "WorkOrderAssignmentPayout"
                ? relatedTo.workOrderAssignment.workOrder.supplier
                : relatedTo?.__typename === "WorkOrderAssignment"
                  ? relatedTo.workOrder.supplier
                  : null;

        if (supplier) {
          return (
            <div>
              <Link
                className="text-blue-500 underline"
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/settings"
                params={(prev) => prev}
                search={{ supplier: supplier.id }}
              >
                {supplier.name}
              </Link>
            </div>
          );
        }

        return t("table.noValue");
      },
      isHidden: !isAdmin,
    },
    {
      id: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("table.statusHeader")}
        />
      ),
      accessorFn: (task) => task.status,
      cell: ({ row }) => {
        const task = row.original;
        return (
          <Badge color={taskStatusMap[task.status]} className="capitalize">
            {t(`status.${task.status}`)}
          </Badge>
        );
      },
    },
    {
      id: "description",
      header: t("table.description"),
      accessorFn: (task) => task.description,
      size: 300,
      cell: ({ row }) => {
        return (
          <Text className="max-w-lg whitespace-normal text-xs">
            {row.original.description}
          </Text>
        );
      },
    },
    {
      id: "due_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("table.due")} />
      ),
      accessorFn: (task) => task.due_at,
      cell: ({ row }) => {
        const task = row.original;
        return (
          <span
            className={cn("text-gray-500", {
              "font-semibold text-red-500": task.status === TaskStatus.Overdue,
            })}
          >
            {formatDateTimeFromNow(task.due_at)}
          </span>
        );
      },
    },
    {
      id: "actions",
      cell: TaskActionCell,
    },
  ];

  return (
    <DataTable
      id={id}
      columns={columns}
      document={taskListQuery}
      searchable={false}
      columnToggles={false}
      title={t("table.title")}
      accessor={(res) => res.tasks}
      getActions={getActions}
      getQueryVariables={({ pagination, sorting }) => {
        const order: QueryTasksOrderByOrderByClause[] = [];

        sorting.map((sort) => {
          if (sort.id === "status") {
            order.push({
              column: QueryTasksOrderByColumn.Status,
              order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
            });
          }

          if (sort.id === "due_at") {
            order.push({
              column: QueryTasksOrderByColumn.DueAt,
              order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
            });
          }
        });

        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          orderBy: order.length ? order : undefined,
          orgOnly: true,
          ...queryVariables,
        };
      }}
    />
  );
}

function TaskActionCell({ row }: CellContext<TaskListItem, any>) {
  const t = useTranslations("task.table");
  const { open: openBookingCompleteDialog } = useDialog(
    BookingCompleteTaskDialog,
  );
  const { open: openProcessRefundDialog } = useDialog(ProcessRefundDialog);
  const { open: openProcessDriverPayoutDialog } = useDialog(
    ProcessDriverPayoutDialog,
  );
  const { open: openSupplierConfirmationDialog } = useDialog(
    BookingSupplierConfirmationReviewDialog,
  );
  const { open: openLineEmailDialog } = useDialog(
    RelocationLineEmailReviewDialog,
  );

  if (row.original.type === TaskType.ConfirmRelocationWasCompleted) {
    return (
      <div className="flex gap-2">
        <Button
          color="green"
          onClick={() => {
            openBookingCompleteDialog({
              task: row.original,
              tab: "completed",
            });
          }}
        >
          {t("complete")}
        </Button>
        <Button
          onClick={() => {
            openBookingCompleteDialog({
              task: row.original,
              tab: "not_completed",
            });
          }}
          color="red"
        >
          {t("cancel")}
        </Button>
      </div>
    );
  }

  if (
    row.original.type === TaskType.ProcessFuelRefund ||
    row.original.type === TaskType.ProcessFerryRefund
  ) {
    const bookingId =
      row.original.relatedTo?.__typename === "Booking"
        ? row.original.relatedTo.id
        : null;

    if (!bookingId) {
      return null;
    }

    const refundType =
      row.original.type === TaskType.ProcessFuelRefund
        ? RefundType.Fuel
        : RefundType.Ferry;

    return (
      <Button
        color="indigo"
        onClick={() => {
          openProcessRefundDialog({
            bookingId,
            refundType,
          });
        }}
      >
        {t("process")}
      </Button>
    );
  }

  if (row.original.type === TaskType.ProcessDriverPayout) {
    const payoutId =
      row.original.relatedTo?.__typename === "WorkOrderAssignmentPayout"
        ? row.original.relatedTo.id
        : null;

    if (!payoutId) {
      return null;
    }

    return (
      <Button
        color="indigo"
        onClick={() => {
          openProcessDriverPayoutDialog({ payoutId });
        }}
      >
        {t("process")}
      </Button>
    );
  }

  if (row.original.type === TaskType.ReviewDriverReceipts) {
    return <ApproveReceiptsActionCell relatedTo={row.original.relatedTo} />;
  }

  if (row.original.type === TaskType.ConfirmSupplierBooking) {
    return (
      <Button
        color="indigo"
        onClick={() => {
          openSupplierConfirmationDialog({
            confirmationId: row.original.related_to_id,
          });
        }}
      >
        {t("review")}
      </Button>
    );
  }

  if (row.original.type === TaskType.ReviewRelocationLineEmail) {
    return (
      <Button
        color="indigo"
        onClick={() => {
          openLineEmailDialog({ emailId: row.original.related_to_id });
        }}
      >
        {t("review")}
      </Button>
    );
  }

  return null;
}

function ApproveReceiptsActionCell({
  relatedTo,
}: {
  relatedTo: TaskListItem["relatedTo"];
}) {
  const t = useTranslations("task.table");
  const { open: openApproveReceiptsDialog } = useDialog(ApproveReceiptsDialog);

  const assignmentId =
    relatedTo?.__typename === "WorkOrderAssignment" ? relatedTo.id : null;

  if (!assignmentId) {
    return null;
  }

  return (
    <Button
      color="green"
      onClick={() =>
        openApproveReceiptsDialog({ workOrderAssignmentId: assignmentId })
      }
    >
      {t("review")}
    </Button>
  );
}

function RelatedToCell({ row }: CellContext<TaskListItem, any>) {
  const task = row.original;

  if (task.relatedTo?.__typename === "Booking") {
    const bookingId = task.relatedTo.id;
    return (
      <Link
        className="text-blue-500 underline"
        from="/$tenant/$orgId/supplier/{-$supplierId}"
        to={"/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"}
        params={(prev) => ({
          ...prev,
          bookingId,
        })}
        search={() => {
          return {};
        }}
      >
        {task.relatedTo.reference}
      </Link>
    );
  }

  if (task.relatedTo?.__typename === "BookingSupplierConfirmation") {
    const booking = task.relatedTo.booking;

    if (!booking) {
      return <Text>{task.relatedTo.booking_reference}</Text>;
    }

    return (
      <Link
        className="text-blue-500 underline"
        from="/$tenant/$orgId/supplier/{-$supplierId}"
        to={"/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"}
        params={(prev) => ({
          ...prev,
          bookingId: booking.id,
        })}
        search={() => {
          return {};
        }}
      >
        {booking.reference}
      </Link>
    );
  }

  if (task.relatedTo?.__typename === "RelocationLineEmail") {
    return (
      <Text className="text-xs text-gray-500">{task.relatedTo.email_from}</Text>
    );
  }

  if (task.relatedTo?.__typename === "WorkOrderAssignmentPayout") {
    const workOrder = task.relatedTo.workOrderAssignment.workOrder;
    return (
      <Link
        className="text-blue-500 underline"
        from="/$tenant/$orgId/supplier/{-$supplierId}"
        to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
        params={(prev) => ({ ...prev, id: workOrder.id })}
      >
        {workOrder.reference}
      </Link>
    );
  }

  if (task.relatedTo?.__typename === "WorkOrderAssignment") {
    const workOrder = task.relatedTo.workOrder;
    const driverName = task.relatedTo.proDriver.name;
    return (
      <div className="flex flex-col">
        <Link
          className="text-blue-500 underline"
          from="/$tenant/$orgId/supplier/{-$supplierId}"
          to="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id"
          params={(prev) => ({ ...prev, id: workOrder.id })}
        >
          {workOrder.reference}
        </Link>
        <Text className="text-xs text-gray-500">{driverName}</Text>
      </div>
    );
  }
}
